// Sprint 4 Retention-Addendum AC-28 — Anliegen-History (letzte 3) im Dashboard.
// Sichtbar nur für User mit >= 1 abgeschlossenem Anliegen-Run.
//
// Sprint M5.35 (29.07.2026) — Neuer `limit`-Prop:
// * Free-Tier: limit=3 (read-only, spec-gemaess)
// * Pro-Tier:  limit=100 (praktisch unbegrenzt, Soft-Cap fuer UX)
//
// Sprint M5.42 Block B (31.07.2026) — Teil D Mini-Fixes:
// - Native <input type="date"> ersetzt durch shadcn-Calendar (de-DE locale)
// - Empty-State mit CTA „Filter zurücksetzen"

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarIcon, Inbox, Sparkles, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const FOKUSTYP_LABEL = {
  eule: "Eule",
  biene: "Biene",
  katze: "Katze",
  hund: "Hund",
  kein_klarer_verdacht: "Mehrere",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}.${mm}.`;
  } catch (e) {
    return "—";
  }
}

// Wandelt YYYY-MM-DD (String) <-> Date fuer den Calendar.
function toDate(iso) {
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}
function toIso(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function DatePickerField({ value, onChange, testId, placeholder }) {
  const [open, setOpen] = useState(false);
  const selectedDate = toDate(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={testId}
          className={cn(
            "h-8 justify-start text-left font-normal rounded-lg border-slate-300 text-xs",
            !selectedDate && "text-slate-500",
          )}
        >
          <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
          {selectedDate
            ? format(selectedDate, "dd.MM.yyyy", { locale: de })
            : placeholder || "Datum wählen"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          locale={de}
          selected={selectedDate}
          onSelect={(d) => {
            onChange(toIso(d));
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export function AnliegenHistory({ limit = 3, showFilters = false }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalKlar, setTotalKlar] = useState(0);
  const [totalTendenz, setTotalTendenz] = useState(0);
  const [loading, setLoading] = useState(true);
  // Sprint M5.42 (30.07.2026) — Block A7: History-Filter (FT + Datum).
  const [filterFT, setFilterFT] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterUntil, setFilterUntil] = useState("");

  const resetFilters = () => {
    setFilterFT("");
    setFilterFrom("");
    setFilterUntil("");
  };
  const hasActiveFilters = !!(filterFT || filterFrom || filterUntil);

  useEffect(() => {
    let mounted = true;
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    if (filterFT) params.set("focus_type", filterFT);
    if (filterFrom) params.set("date_from", `${filterFrom}T00:00:00Z`);
    if (filterUntil) params.set("date_until", `${filterUntil}T23:59:59Z`);
    api
      .get(`/anliegen/history?${params.toString()}`)
      .then((res) => {
        if (!mounted) return;
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
        setTotalKlar(res.data?.total_klar || 0);
        setTotalTendenz(res.data?.total_tendenz || 0);
      })
      .catch(() => {
        if (mounted) setItems([]);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [limit, filterFT, filterFrom, filterUntil]);

  if (loading) return null;
  // Sprint M5.42 Block B (31.07.2026): Empty-State soll erscheinen, wenn
  // Filter aktiv sind aber keine Ergebnisse — deshalb nicht mehr fruehzeitig
  // returnen, wenn showFilters=true.
  if (items.length === 0 && !showFilters) return null;

  return (
    <section
      id="bruecken-history"
      data-testid="anliegen-history"
      className="rounded-2xl bg-white border border-[var(--fts-neutral-200)] p-6 sm:p-7"
    >
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h2
          data-testid="anliegen-history-headline"
          className="text-lg sm:text-xl font-extrabold tracking-tight text-[var(--fts-text-primary)]"
        >
          Deine letzten Flexpfade
        </h2>
        {total > 0 && (
          <span
            data-testid="anliegen-history-counter"
            className="text-xs text-[var(--fts-text-muted)]"
          >
            {totalTendenz > 0
              ? `Du hast ${total} Flexpfade gelöst — davon ${totalKlar} klar, ${totalTendenz} mit Tendenz.`
              : `Du hast ${total} Flexpfade gelöst.`}
          </span>
        )}
      </div>
      {showFilters && (
        <div
          data-testid="anliegen-history-filters"
          className="mb-4 flex flex-wrap gap-3 items-end p-3 rounded-xl bg-slate-50 border border-slate-200"
        >
          <label className="flex flex-col text-xs">
            <span className="font-semibold text-slate-700 mb-1">Fokustyp</span>
            <select
              value={filterFT}
              onChange={(e) => setFilterFT(e.target.value)}
              data-testid="history-filter-focus-type"
              className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
            >
              <option value="">Alle</option>
              <option value="eule">Eule</option>
              <option value="biene">Biene</option>
              <option value="katze">Katze</option>
              <option value="hund">Hund</option>
            </select>
          </label>
          <div className="flex flex-col text-xs">
            <span className="font-semibold text-slate-700 mb-1">Von</span>
            <DatePickerField
              value={filterFrom}
              onChange={setFilterFrom}
              testId="history-filter-date-from"
              placeholder="Von"
            />
          </div>
          <div className="flex flex-col text-xs">
            <span className="font-semibold text-slate-700 mb-1">Bis</span>
            <DatePickerField
              value={filterUntil}
              onChange={setFilterUntil}
              testId="history-filter-date-until"
              placeholder="Bis"
            />
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={resetFilters}
              data-testid="history-filter-clear"
              className="text-xs font-semibold text-[#4563BB] hover:underline"
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      )}
      {items.length === 0 ? (
        <div
          data-testid="anliegen-history-empty"
          className="flex flex-col items-center text-center py-10 px-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-dashed border-slate-200 dark:border-slate-700"
        >
          {hasActiveFilters ? (
            /* Filter aktiv, aber keine Treffer — schmaler Reset-Empty-State */
            <>
              <div className="w-12 h-12 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center mb-3">
                <Inbox className="w-6 h-6 text-slate-400 dark:text-slate-500" />
              </div>
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Noch keine Flexpfade. Passen deine Filter?
              </p>
              <button
                type="button"
                onClick={resetFilters}
                data-testid="anliegen-history-empty-reset"
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#4563BB] hover:bg-[#3a54a2] px-4 py-2 text-xs font-semibold text-white"
              >
                Filter zurücksetzen
              </button>
            </>
          ) : (
            /* Sprint V1.7 Launch-Conversion-QW#2 (Do10.09.2026, 17:45) —
               einladender First-Run-Empty-State fuer neue User ohne Anliegen. */
            <>
              <div className="w-16 h-16 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center mb-4 shadow-sm">
                <Sparkles className="w-8 h-8 text-[#4563BB] dark:text-blue-300" aria-hidden="true" />
              </div>
              <h3
                data-testid="anliegen-history-empty-headline"
                className="text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white"
              >
                Noch keine Anliegen
              </h3>
              <p
                data-testid="anliegen-history-empty-sub"
                className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed max-w-md"
              >
                Beschreib deine erste Situation und entdecke, welcher Fokustyp bei deinem Gegenüber dominiert.
              </p>
              <button
                type="button"
                onClick={() => navigate("/dashboard/anliegen")}
                data-testid="anliegen-history-empty-cta"
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#4563BB] hover:bg-[#3a54a2] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5"
              >
                Erstes Anliegen erstellen
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      ) : (
        <ul className="divide-y divide-[var(--fts-neutral-100)]">
          {items.map((item) => {
            // Sprint 4c-4 D.1.14 — neue Runs zeigen Flexpfad-Vorschau,
            // Legacy-Runs (is_legacy: true) zeigen „4 Sätze generiert".
            const isLegacy = item.is_legacy || !item.bridge_text;
            const previewLine = isLegacy
              ? "4 Sätze generiert (Legacy)"
              : "Flexpfad generiert";
            return (
              <li
                key={item.id}
                data-testid={`anliegen-history-item-${item.id}`}
                data-legacy={isLegacy ? "true" : "false"}
                className="py-3 flex items-start gap-3"
              >
                <span className="flex-shrink-0 text-xs font-mono text-[var(--fts-text-muted)] w-12 pt-1">
                  {formatDate(item.created_at)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[var(--fts-text-primary)] truncate">
                    &bdquo;{item.situation}&ldquo;
                  </p>
                  <p className="mt-0.5 text-xs text-[var(--fts-text-muted)]">
                    Verdacht:{" "}
                    <span className="font-semibold">
                      {FOKUSTYP_LABEL[item.verdacht_fokustyp] || "—"}-Anteil
                    </span>
                    {" · "}
                    {previewLine}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <Link
        to="/dashboard/anliegen"
        data-testid="anliegen-history-all-link"
        className="inline-block mt-4 text-sm font-semibold text-[var(--fts-primary-deep)] hover:underline"
      >
        Alle Flexpfade ansehen →
      </Link>
    </section>
  );
}

export default AnliegenHistory;

